import { FieldBase, useAppForm } from '@/components/form';
import { Button, Label } from '@e2n/ui';
import { ArbeitsBereichBadge } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/ArbeitsBereichBadge';
import { SelectTauschPartner } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/SelectTauschPartner';
import { SheetContext } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/SheetContext';
import { useUpdateSchichttauschantrag } from '@/domain/navigation/benachrichtigungen/hooks/useUpdateSchichttauschAntrag';
import { Mitarbeiter } from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { SchichttauschAntrag } from '@/domain/navigation/benachrichtigungen/types/SchichttauschAntrag';
import { Navigation_SchichttauschantragStatus } from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';
import { faCheck, faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

// eslint-disable-next-line max-lines-per-function
export function AntragsFormular({ antrag }: { antrag: SchichttauschAntrag }) {
  const { setOpen } = useContext(SheetContext);
  const [update, result] = useUpdateSchichttauschantrag();
  const { t } = useTranslation();

  const formSchema = z
    .object({
      tauschPartner: z.custom<Mitarbeiter>().nullable(),
      bemerkung: z.string(),
    })
    .refine((data) => data.tauschPartner !== null, {
      message: t(
        'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.errors.tauschPartnerRequired',
      ),
      path: ['tauschPartner'],
    });

  const form = useAppForm({
    defaultValues: {
      tauschPartner: null,
      bemerkung: '',
    } satisfies z.infer<typeof formSchema> as z.infer<typeof formSchema>,
    validators: {
      onChange: formSchema,
    },
    onSubmit: async ({ value }) => {
      const result = await update({
        input: {
          schichttauschAntragId: antrag.id,
          status: Navigation_SchichttauschantragStatus.Genehmigt,
          tauschPartnerId: value.tauschPartner?.id,
          bemerkungManager: value.bemerkung,
        },
      });
      if (result.data?.navigation_updateSchichttauschAntrag.error) {
        toast({
          title: result.data.navigation_updateSchichttauschAntrag.error.message,
        });
      }
      if (result.data?.navigation_updateSchichttauschAntrag.success) {
        toast({
          title: t(
            'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.genehmigen.title',
          ),
          description: t(
            'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.genehmigen.description',
          ),
        });
        setOpen(false);
      }
    },
  });

  const handleReject = async () => {
    const result = await update({
      input: {
        schichttauschAntragId: antrag.id,
        status: Navigation_SchichttauschantragStatus.Abgelehnt,
        bemerkungManager: form.state.values.bemerkung,
      },
    });

    if (result.data?.navigation_updateSchichttauschAntrag.error) {
      toast({
        title: result.data.navigation_updateSchichttauschAntrag.error.message,
      });
    }
    if (result.data?.navigation_updateSchichttauschAntrag.success) {
      toast({
        title: t(
          'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.ablehnen.title',
        ),
        description: t(
          'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.ablehnen.description',
        ),
      });
      setOpen(false);
    }
  };
  return (
    <form.AppForm>
      <form
        id="updateTauschantrag"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="flex flex-col gap-6 sm:w-80">
        <div className="flex flex-col gap-2">
          <Label className="text-neutrals-900 text-xs font-bold">
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.arbeitsbereich',
            )}
          </Label>
        </div>
        <ArbeitsBereichBadge arbeitsbereich={antrag.schicht.arbeitsbereich} />
        <div className="flex flex-col gap-2">
          <Label className="text-neutrals-900 text-xs font-bold">
            {' '}
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.datum',
            )}
          </Label>

          <span className="text-neutrals-900 text-sm">
            {dayjs(antrag.schicht.beginn.toDate())
              .locale('de')
              .format('ddd, DD. MMMM, HH:mm')}
            {' - '}
            {antrag.schicht.ende.format('HH:mm')} Uhr
          </span>
        </div>

        <form.AppField name="tauschPartner">
          {(field) => (
            <FieldBase
              label={t(
                'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.tauschPartnerSelect.label',
              )}
              isRequired>
              <SelectTauschPartner
                antrag={antrag}
                value={field.state.value}
                onSelect={(m) => field.handleChange(m)}
              />
            </FieldBase>
          )}
        </form.AppField>

        <form.AppField name="bemerkung">
          {(field) => (
            <field.TextArea
              label={t(
                'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.deineBemerkung',
              )}
              maxLength={250}
              className="h-24"
            />
          )}
        </form.AppField>
        <div className="flex flex-row gap-4 w-full justify-end">
          <Button
            variant="destructive"
            className="flex gap-2"
            onClick={handleReject}
            disabled={result.loading}>
            <FontAwesomeIcon icon={faXmarkLarge} />
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.ablehnen',
            )}
          </Button>
          <form.SubmitButton
            disabled={result.loading}
            buttonText={t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.genehmigen',
            )}
            leadingIcon={faCheck}
          />
        </div>
      </form>
    </form.AppForm>
  );
}
